import {
  UserDeviceType,
  useRequestOauthToken,
  useApiContext,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { useToasterContext } from "@tandemdiabetes/sugarmate-react-components"
import { useNavigate } from "@tanstack/react-router"
import {
  useCallback,
  useMemo,
} from "react"

import { RegistrationFlow } from "./RegistrationFlow"
import type { RegistrationForm } from "./RegistrationForm"

import {
  useAuthContext,
  useCountrySelectionContext,
  useSessionStorageContext,
} from "src/contexts"
import { useCreatePhoneWithAccessToken } from "src/hooks"
import {
  CreatePhoneErrorToast,
  SignInAfterSignUpErrorToast,
} from "src/toasts"
import {
  injectProps,
  isUS,
  getSugarmateError,
} from "src/utils"

const clientId: string = process.env.REACT_APP_SUGARMATE_OAUTH_CLIENT_ID ?? ""

export function SignUp() {
  const { country } = useCountrySelectionContext()
  const { baseURL } = useApiContext()
  const { setLoginRedirect } = useSessionStorageContext()
  const { setAuthCookie } = useAuthContext()
  const requestOauthToken = useRequestOauthToken()
  const { showToast } = useToasterContext()
  const navigate = useNavigate()
  const createPhone = useCreatePhoneWithAccessToken()

  const onAccountCreate = useMemo(
    () => (!requestOauthToken || !baseURL)
      ? undefined
      : async (registrationForm: RegistrationForm) => {
        setLoginRedirect(
          isUS(country?.country_code)
            ? "/onboarding/get_started"
            : "/onboarding/units_of_measure",
        )

        try {
          const response = await requestOauthToken({
            username: registrationForm.emailAddress,
            password: registrationForm.password,
            client_id: clientId,
            grant_type: "password",
            device_type: UserDeviceType.Web,
          })

          if (registrationForm.phoneNumber) {
            void createPhone?.(
              {
                phone_number: registrationForm.phoneNumber,
                is_primary: true,
              },
              response.data.access_token,
            ).catch((e) => {
              const sugarmateError = getSugarmateError(e)

              const ErrorToast = injectProps(
                CreatePhoneErrorToast,
                {
                  code: sugarmateError?.code,
                  status: sugarmateError?.status,
                },
              )

              showToast(
                "create-phone-error-toast",
                ErrorToast,
              )
            })
          }

          setAuthCookie(
            response.data,
            baseURL,
          )
        } catch {
          showToast(
            "sign-in-after-sign-up-error-toast",
            SignInAfterSignUpErrorToast,
          )

          void navigate({ to: "/sign_in" })
        }
      },
    [
      setLoginRedirect,
      requestOauthToken,
      setAuthCookie,
      navigate,
      createPhone,
      showToast,
      baseURL,
      country,
    ],
  )

  const goBack = useCallback(
    () => {
      void navigate({ to: "/" })
    },
    [navigate],
  )

  return (
    <RegistrationFlow
      onAccountCreate={onAccountCreate}
      goBack={goBack}
    />
  )
}
